export type WarehouseSearchViewMode = 'map' | 'list';

export interface WarehouseSearchFilters {
  inputValue: string;
  viewMode: WarehouseSearchViewMode;
  currency: string;
  specialCapabilities: string[];
  valueAddingServices: string[];
  systemsAndSecurity: string[];
  temperatureControls: string[];
  sectorExperience: string[];
  fulfilmentServices: string[];
  materialHandlingEquipment: string[];
  accreditationsAndMemberships: string[];
  storageCapacityUnitOfMeasure: string;
  minAvailableStorageCapacity: string;
  requiresRacking: boolean;
  minRackingHeight: string;
  minRackingWidth: string;
  minRackingDepth: string;
  fromPrice: string;
  toPrice: string;
}

export const WAREHOUSE_SEARCH_FILTERS_STORAGE_KEY =
  'flox.warehouseSearch.filters.v1';

const asString = (value: unknown) => (typeof value === 'string' ? value : '');

const asStringList = (value: unknown) =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];

const asViewMode = (value: unknown): WarehouseSearchViewMode =>
  value === 'list' ? 'list' : 'map';

function normaliseFilters(value: unknown): WarehouseSearchFilters {
  const source = (value && typeof value === 'object' ? value : {}) as Record<
    string,
    unknown
  >;

  return {
    inputValue: asString(source.inputValue),
    viewMode: asViewMode(source.viewMode),
    currency: asString(source.currency),
    specialCapabilities: asStringList(source.specialCapabilities),
    valueAddingServices: asStringList(source.valueAddingServices),
    systemsAndSecurity: asStringList(source.systemsAndSecurity),
    temperatureControls: asStringList(source.temperatureControls),
    sectorExperience: asStringList(source.sectorExperience),
    fulfilmentServices: asStringList(source.fulfilmentServices),
    materialHandlingEquipment: asStringList(source.materialHandlingEquipment),
    accreditationsAndMemberships: asStringList(
      source.accreditationsAndMemberships
    ),
    storageCapacityUnitOfMeasure: asString(source.storageCapacityUnitOfMeasure),
    minAvailableStorageCapacity: asString(source.minAvailableStorageCapacity),
    requiresRacking: source.requiresRacking === true,
    minRackingHeight: asString(source.minRackingHeight),
    minRackingWidth: asString(source.minRackingWidth),
    minRackingDepth: asString(source.minRackingDepth),
    fromPrice: asString(source.fromPrice),
    toPrice: asString(source.toPrice),
  };
}

export function createEmptyWarehouseSearchFilters(): WarehouseSearchFilters {
  return normaliseFilters(null);
}

export function loadWarehouseSearchFilters(): WarehouseSearchFilters {
  try {
    const stored = window.localStorage.getItem(
      WAREHOUSE_SEARCH_FILTERS_STORAGE_KEY
    );
    if (!stored) return createEmptyWarehouseSearchFilters();
    return normaliseFilters(JSON.parse(stored));
  } catch {
    return createEmptyWarehouseSearchFilters();
  }
}

export function saveWarehouseSearchFilters(
  filters: WarehouseSearchFilters
): boolean {
  try {
    window.localStorage.setItem(
      WAREHOUSE_SEARCH_FILTERS_STORAGE_KEY,
      JSON.stringify(filters)
    );
    return true;
  } catch {
    return false;
  }
}

export function clearWarehouseSearchFilters(): boolean {
  try {
    window.localStorage.removeItem(WAREHOUSE_SEARCH_FILTERS_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}
